import { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Input, SelectPicker, Message, IconButton, Tag, Divider } from 'rsuite';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { useCustomTheme } from '../../../hooks/useCustomTheme';
import { toastError, toastSuccess } from '../../../toasts';
import { CustomInput } from '../../../components/CustomInput';
import { createTemplate, updateTemplate } from '../../../services/uniservice/uniservice.service';
import TemplateBubble from './TemplateBubble';
import {
  CATEGORIES,
  LANGUAGES,
  HEADER_TYPES,
  BUTTON_TYPES,
  numberedVars,
  validateTemplate,
} from './constants';
import { styles } from './styles';

const emptyForm = {
  name: '',
  category: 'UTILITY',
  language: 'pt_BR',
  headerType: 'NONE',
  headerContent: '',
  bodyText: '',
  footerText: '',
  buttons: [],
  examples: {},
};

function fromTemplate(template) {
  const bodyVars = numberedVars(template.bodyText);
  const examples = {};
  bodyVars.forEach((n, i) => {
    examples[n] = (template.exampleValues || [])[i] || '';
  });
  return {
    name: template.name || '',
    category: template.category || 'UTILITY',
    language: template.language || 'pt_BR',
    headerType: template.headerType || 'NONE',
    headerContent: template.headerContent || '',
    bodyText: template.bodyText || '',
    footerText: template.footerText || '',
    buttons: Array.isArray(template.buttons) ? template.buttons : [],
    examples,
  };
}

export default function CreateEditTemplateModal({ open, onClose, template, companyId, onSaved }) {
  const { theme } = useCustomTheme();
  const isEdit = Boolean(template);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(template ? fromTemplate(template) : emptyForm);
  }, [open, template]);

  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
  const bodyVars = useMemo(() => numberedVars(form.bodyText), [form.bodyText]);
  const problems = useMemo(() => validateTemplate(form, isEdit), [form, isEdit]);

  const addVar = () => {
    const next = (bodyVars[bodyVars.length - 1] || 0) + 1;
    setForm((prev) => ({ ...prev, bodyText: `${prev.bodyText}{{${next}}}` }));
  };

  const addButton = (type) =>
    setForm((prev) => ({ ...prev, buttons: [...prev.buttons, { type, text: '', url: '', phone_number: '' }] }));

  const setButton = (index, key, value) =>
    setForm((prev) => ({
      ...prev,
      buttons: prev.buttons.map((b, i) => (i === index ? { ...b, [key]: value } : b)),
    }));

  const removeButton = (index) =>
    setForm((prev) => ({ ...prev, buttons: prev.buttons.filter((_, i) => i !== index) }));

  const handleSave = async () => {
    if (problems.length) {
      toastError('Revise os campos destacados antes de salvar');
      return;
    }
    const exampleValues = bodyVars.map((n) => form.examples[n]);
    const buttons = form.buttons.map((b) => ({
      type: b.type,
      text: b.text,
      ...(b.type === 'URL' ? { url: b.url } : {}),
      ...(b.type === 'PHONE_NUMBER' ? { phone_number: b.phone_number } : {}),
    }));
    const payload = {
      headerType: form.headerType,
      headerContent: form.headerType === 'NONE' ? '' : form.headerContent,
      bodyText: form.bodyText.trim(),
      footerText: form.footerText.trim(),
      buttons,
      exampleValues,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await updateTemplate(companyId, template.id, payload);
      } else {
        await createTemplate(companyId, {
          name: form.name.trim(),
          category: form.category,
          language: form.language,
          ...payload,
        });
      }
      toastSuccess(isEdit ? 'Template atualizado' : 'Template enviado para aprovação da Meta');
      onSaved?.();
      onClose();
    } catch {
      toastError('Não foi possível salvar o template');
    } finally {
      setSaving(false);
    }
  };

  const isMedia = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerType);

  return (
    <Modal open={open} onClose={onClose} size="lg" overflow>
      <Modal.Header>
        <Modal.Title>{isEdit ? 'Editar template' : 'Novo template'}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={styles.modalBody}>
        {/* Formulário */}
        <div style={styles.modalForm}>
          {isEdit ? (
            <div style={styles.readonlyCard(theme)}>
              <strong>{form.name}</strong>
              <span style={styles.readonlyMeta(theme)}>{form.category} · {form.language}</span>
            </div>
          ) : (
            <>
              <CustomInput label="Nome do template" value={form.name} onChange={set('name')} placeholder="ex: confirmacao_pedido" />
              <Field label="Categoria" theme={theme}>
                <SelectPicker data={CATEGORIES} value={form.category} onChange={set('category')} cleanable={false} searchable={false} block />
              </Field>
              <Field label="Idioma" theme={theme}>
                <SelectPicker data={LANGUAGES} value={form.language} onChange={set('language')} cleanable={false} searchable={false} block />
              </Field>
            </>
          )}

          <Field label="Cabeçalho (opcional)" theme={theme}>
            <SelectPicker data={HEADER_TYPES} value={form.headerType} onChange={set('headerType')} cleanable={false} searchable={false} block />
          </Field>
          {form.headerType === 'TEXT' && (
            <Input value={form.headerContent} onChange={set('headerContent')} maxLength={60} placeholder="Título do cabeçalho" />
          )}
          {isMedia && (
            <Input value={form.headerContent} onChange={set('headerContent')} placeholder="URL de exemplo da mídia" />
          )}

          <Field label="Corpo da mensagem" theme={theme}>
            <Input as="textarea" rows={5} value={form.bodyText} onChange={set('bodyText')} placeholder="Olá {{1}}, o seu pedido foi confirmado!" />
            <div style={{ marginTop: 8 }}>
              <Tag as="button" onClick={addVar} style={styles.varTag(theme)}>+ Adicionar variável</Tag>
            </div>
          </Field>

          {bodyVars.map((n) => (
            <CustomInput
              key={n}
              label={`Exemplo para {{${n}}}`}
              value={form.examples[n] || ''}
              onChange={(v) => setForm((prev) => ({ ...prev, examples: { ...prev.examples, [n]: v } }))}
              placeholder={`Valor de exemplo para {{${n}}}`}
            />
          ))}

          <Field label="Rodapé (opcional)" theme={theme}>
            <Input value={form.footerText} onChange={set('footerText')} maxLength={60} placeholder="Ex: Responda SAIR para cancelar" />
          </Field>

          <Field label="Botões (opcional)" theme={theme}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {BUTTON_TYPES.map((bt) => (
                <Tag key={bt.value} as="button" onClick={() => addButton(bt.value)} style={styles.varTag(theme)}>
                  <FaPlus size={10} /> {bt.label}
                </Tag>
              ))}
            </div>
          </Field>
          {form.buttons.map((b, i) => (
            <div key={i} style={styles.buttonEditor(theme)}>
              <div style={styles.buttonEditorHead}>
                <span style={styles.readonlyMeta(theme)}>{BUTTON_TYPES.find((t) => t.value === b.type)?.label}</span>
                <IconButton size="xs" appearance="subtle" icon={<FaTrash />} onClick={() => removeButton(i)} />
              </div>
              <Input size="sm" value={b.text} onChange={(v) => setButton(i, 'text', v)} maxLength={25} placeholder="Texto do botão" />
              {b.type === 'URL' && (
                <Input size="sm" value={b.url} onChange={(v) => setButton(i, 'url', v)} placeholder="https://..." style={{ marginTop: 6 }} />
              )}
              {b.type === 'PHONE_NUMBER' && (
                <Input size="sm" value={b.phone_number} onChange={(v) => setButton(i, 'phone_number', v)} placeholder="+5563..." style={{ marginTop: 6 }} />
              )}
            </div>
          ))}

          {problems.length > 0 && (
            <Message type="warning" showIcon header="Ajuste antes de salvar">
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {problems.slice(0, 5).map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </Message>
          )}
        </div>

        {/* Preview */}
        <div style={styles.modalPreview(theme)}>
          <div style={styles.previewLabel(theme)}>Pré-visualização</div>
          <TemplateBubble
            headerType={form.headerType}
            headerContent={form.headerContent}
            bodyText={form.bodyText}
            footerText={form.footerText}
            buttons={form.buttons}
            getExample={(n) => form.examples[n]}
          />
          <Divider style={{ margin: '16px 0 8px' }} />
          <div style={styles.previewStats(theme)}>
            <span>{(form.bodyText || '').length}/1024 caracteres</span>
            <span>{bodyVars.length} variáveis</span>
            <span>{form.buttons.length} botões</span>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button appearance="subtle" onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          appearance="primary"
          onClick={handleSave}
          loading={saving}
          disabled={problems.length > 0}
          style={{ backgroundColor: theme.colors.primary }}
        >
          {isEdit ? 'Salvar alterações' : 'Enviar para aprovação'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function Field({ label, theme, children }) {
  return (
    <div>
      <div style={styles.fieldLabel(theme)}>{label}</div>
      {children}
    </div>
  );
}
